import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, Calendar, Users, CheckCircle2, TrendingUp, Menu, X, LogOut, 
  Settings, UserPlus, ChevronDown, Crown, Shield, User, Zap 
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_hoop-tracker-7/artifacts/tisywt9m_attendance%20tracker%20logo.png';

const Layout = ({ children }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const { user, logout, isOwner, canManageAthletes } = useAuth();

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubscriptionTier(response.data.subscription?.tier || 'free');
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const isPro = subscriptionTier === 'pro';
  const isSuperAdmin = user?.role === 'super_admin';

  // Build nav items based on user permissions
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Home, show: true },
    { path: '/programs', label: 'Programs', icon: Calendar, show: isOwner || isSuperAdmin },
    { path: '/athletes', label: 'Athletes', icon: Users, show: canManageAthletes || isSuperAdmin },
    { path: '/attendance', label: 'Attendance', icon: CheckCircle2, show: true },
    { path: '/reports', label: 'Reports', icon: TrendingUp, show: true },
    { path: '/team', label: 'Team', icon: UserPlus, show: (isOwner && isPro) || isSuperAdmin },
    { path: '/admin', label: 'Admin', icon: Shield, show: isSuperAdmin }
  ].filter(item => item.show);

  const isActive = (path) => location.pathname === path;

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield className="h-3 w-3" />;
      case 'owner': return <Crown className="h-3 w-3" />;
      case 'manager': return <Shield className="h-3 w-3" />;
      default: return <User className="h-3 w-3" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-800';
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src={LOGO_URL} 
                alt="Athlete Attendance App" 
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-lg font-bold" style={{ color: '#2E3E6F' }}>Athlete Attendance</h1>
                <p className="text-xs text-gray-600">Track your team</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              <div className="flex space-x-1 mr-4">
                {navItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                        isActive(item.path)
                          ? 'text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      style={isActive(item.path) ? { backgroundColor: '#2E3E6F' } : {}}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
              
              {/* User Menu */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="flex items-center space-x-2">
                      <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center">
                        {getRoleIcon(user.role)}
                      </div>
                      <span className="max-w-24 truncate">{user.name}</span>
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-gray-500">{user.email}</p>
                        <div className="flex items-center space-x-2">
                          <Badge className={`w-fit text-xs ${getRoleBadgeColor(user.role)}`}>
                            {user.role === 'super_admin' ? 'Super Admin' : user.role === 'owner' ? 'Owner' : user.role === 'manager' ? 'Manager' : 'Assistant'}
                          </Badge>
                          {isPro ? (
                            <Badge className="w-fit text-xs bg-purple-100 text-purple-800">Pro</Badge>
                          ) : (
                            <Badge className="w-fit text-xs bg-gray-100 text-gray-600">Free</Badge>
                          )}
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {isSuperAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center cursor-pointer text-red-600">
                          <Shield className="h-4 w-4 mr-2" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {(isOwner || isSuperAdmin) && (
                      <DropdownMenuItem asChild>
                        <Link to="/subscription" className="flex items-center cursor-pointer">
                          <Zap className="h-4 w-4 mr-2" />
                          {isPro ? 'Subscription' : 'Upgrade to Pro'}
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="flex items-center cursor-pointer">
                        <Settings className="h-4 w-4 mr-2" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-4 py-2 space-y-1">
              {user && (
                <div className="flex items-center space-x-3 px-3 py-2 mb-2 bg-gray-50 rounded-lg">
                  <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                    {getRoleIcon(user.role)}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{user.name}</p>
                    <Badge className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                      {user.role === 'owner' ? 'Owner' : user.role === 'manager' ? 'Manager' : 'Assistant'}
                    </Badge>
                  </div>
                </div>
              )}
              {navItems.map(item => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${
                      isActive(item.path)
                        ? 'text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                    style={isActive(item.path) ? { backgroundColor: '#2E3E6F' } : {}}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
              <Link
                to="/settings"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg ${
                  isActive('/settings')
                    ? 'text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                style={isActive('/settings') ? { backgroundColor: '#2E3E6F' } : {}}
              >
                <Settings className="h-5 w-5" />
                <span className="font-medium">Settings</span>
              </Link>
              {user && (
                <button
                  onClick={() => { logout(); setIsMobileMenuOpen(false); }}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 w-full text-left"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Logout</span>
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <img 
                src={LOGO_URL} 
                alt="Athlete Attendance App" 
                className="h-8 w-auto object-contain"
              />
              <p className="text-sm text-gray-600">Athlete Attendance App</p>
            </div>
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <div className="flex space-x-4 text-sm">
                <Link to="/terms" className="text-gray-600 hover:text-gray-900 hover:underline">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-gray-600 hover:text-gray-900 hover:underline">
                  Privacy Policy
                </Link>
                <a href="mailto:info@attendancetracker.ws" className="text-gray-600 hover:text-gray-900 hover:underline">
                  Contact
                </a>
              </div>
              <p className="text-sm text-gray-600">
                © 2025 Anglin Sports Consultancy. Built for coaches.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
